import { type Writable, writable } from 'svelte/store'
import { browser } from '$app/environment'
import type { CartConfigurableProduct, CartProduct, Suggestion } from '$lib/js/types'
import type { BaseBrand, BaseProduct, BrandLink, Category, ConfigurableChild, FullBrand } from '$lib/js/elasticsearch/types'
import type { Address, Country, CustomerInfo, CustomerToken, Order } from './magento/types'
import type { Cart } from '$lib/js/magento/cart/types'
import type { Wishlist } from './supabase/wishlists'

function getLocalStorage<T>(key: string, default_value: T): string | T

/**
 * Get a key in the browser's localStorage.
 * If not in the browser or if the localStorage doesn't contain the key,
 * return the default value.
 */
function getLocalStorage(key: string, default_value: any = null) {
	if (!browser) {
		return default_value
	}

	try {
		const value = localStorage.getItem(key)
		if (value !== null) {
			return value
		}
	} catch (e) {
		return default_value
	}
}

/**
 * Get a key in the browser's localStorage and parse it as JSON.
 * If not in the browser or if the localStorage doesn't contain the key or the value is not valid JSON,
 * return the default value.
 */
function getLocalStorageJSON(key: string, default_value: any = null) {
	const isDefault: object = {} // unique value
	const value = getLocalStorage(key, isDefault)

	if (value === isDefault) {
		return default_value
	}

	try {
		return JSON.parse(value as string)
	} catch (e) {
		console.warn('Error while parsing local storage key', key, e)
		return default_value
	}
}

/* ================================ */
/*   MENU
/* ================================ */

/* Store hero menu */
export const storeLoggedUser = writable(false)

/* ================================ */
/*   MAGENTO
/* ================================ */

export const storeMagentoCountries = writable([] as Country[])


/* ================================ */
/*   MODALS
/* ================================ */

/* Store hero menu */
export const storeHeroMenu = writable('')
export const storeMobileMenu = writable(false)

/* Store cats menu */
export const storeInfoMenu = writable('')

/* Store login modal */
export const storeLoginModal = writable(false)

// /* Store shops menu */
// export const storeShopsMenu = writable(false)

/* Store remove product from cart modal */
export const storeRemoveModal = writable(0)

/* Store minicart modal */
export const storeMiniCartModal = writable(false)

/* Product page no stock modal */
export const storeStockModal: Writable<string | number> = writable('')

/* No-stock modal */
export const storeNoStockModal = writable(false)

/* Modal for sharing the cart */
export const storeShareCartModal = writable(false)

/* Modal used in case the incoming cart is different from existing one */
export const storeIncomingCartModal = writable(false)

/* No stock modal items */
export const storeNoStockItems: Writable<{ name: string }[]> = writable([])

/* Values modal */
export const storeValueModal = writable(false)

/* Compensation modal */
export const storeCompensationModal = writable(false)

/* Edit Address modal */
export const storeSaveAddressModal = writable('') /* 'edit' or 'new' or '' ('' for modal closed)*/
export const storeAddressToEdit: Writable<Address | undefined> = writable(undefined)

/* Delete Address modal */
export const storeDeleteAddressModal: Writable<Address | undefined> = writable(undefined) // open if storeDeleteAddressModal is a number, close if it is ''

/* Edit Account Info modal */
export const storeEditAccountInfoModal = writable(false)

/* Change Password Modal */
export const storeChangePasswordModal = writable(false)

/* Donwload Invoice Modal */
export const storeDownloadInvoiceModal = writable(false)

/* Donwload Invoice Modal */
export const storeOrderIssuesModal = writable('') // 'tracking-issue' || 'issue' || 'not-found' || 'return' || 'incorrect-data' ||

/* Find Order Modal */
export const storeFindOrderModal = writable(false)

/* Success Modal */
export const storeSuccessModal = writable(false)

/* Find Order Modal */
export const storeIssueOrder = writable({} as {
	order: Order,
	products: (BaseProduct | null)[],
	shipments: any
} | undefined)


/* Find Order Modal */
export const storeUploadFiles = writable('' as string)

/* Store Chat Modal */
export const storeChatModal = writable(false)


/* ================================ */
/*   SNACKBAR
/* ================================ */

/* Store snackbar */
export const storeSnackbar = writable({
	message: '',
	show: false,
})

/* ================================ */
/*   ACCOUNT
/* ================================ */

/* Store customer token */
// TODO: use null as the default value, not {}
export const storeCustomerToken: Writable<CustomerToken | null> = writable({} as CustomerToken)
export const storeCustomerTokenSet: Writable<boolean> = writable(false)

/* Store customer info */
// TODO: use null as the default value, not {}
export const storeCustomerInfo: Writable<CustomerInfo | null> = writable({} as CustomerInfo | null)

/* Store customer orders */
// TODO: remove the .items intermediary
// TODO: use null as the default value, not {}
export const storeCustomerOrders: Writable<{ items: Order[] }> = writable({} as { items: Order[] })

// /* Store current page for orders */
// export const storeCurrentOrdersPage: Writable<number> = writable(0)

/* ================================ */
/*   CART
/* ================================ */

/* Store products added to cart */
export const storeCartItems: Writable<CartProduct[]> = writable(
	getLocalStorageJSON('storeCartItems', []),
)
storeCartItems.subscribe((cart_items) => {
	/*
	FIXME: we have several issues in prod on this line.
		- "The operation is insecure" (SVELTE-FRONT-PROD-PN)
		- "Failed to read the 'localStorage' property from 'Window': Access is denied for this document." (SVELTE-FRONT-PROD-R8)
		- "NS_ERROR_FILE_CORRUPTED" (SVELTE-FRONT-PROD-R7)
	 */
	if (browser) return (localStorage.storeCartItems = JSON.stringify(cart_items))
})

/* Store cart */
export const storeUserCart: Writable<Cart> = writable(getLocalStorageJSON('storeUserCart', {}))
storeUserCart.subscribe((user_cart) => {
	if (browser) return (localStorage.storeUserCart = JSON.stringify(user_cart))
})

/* Store cart errors */
export const storeCartError = writable('')

/* ================================ */
/*   PRODUCTS
/* ================================ */

/* Store selected configurable child */
export const storeConfigurableChild: Writable<undefined | ConfigurableChild> = writable(undefined)

/* Store visited products */
const maxVisitedProducts = 20
export const storeVisitedProducts: Writable<BaseProduct[]> = writable(
	getLocalStorageJSON('storeVisitedProducts', []),
)
storeVisitedProducts.subscribe(function(visited_products) {
	if (browser) {
		return (localStorage.storeVisitedProducts = JSON.stringify(
			visited_products.slice(0, maxVisitedProducts),
		))
	}
})

/* ================================ */
/*   COOKIE NOTICE
/* ================================ */
type CookieConsent = Record<'ad_storage' | 'analytics_storage' | 'ad_user_data' | 'ad_personalization', 'granted' | 'denied'>

/**
 * Parse the cookie consent local storage entry.
 * This supports both old-style values ("accepted", "rejected") and new-style ones
 */
let loadOldConsent = (value: string): CookieConsent | {} | null => {
	if (value == 'accepted') {
		return {
			ad_storage: 'granted',
			analytics_storage: 'granted',
			ad_user_data: 'granted',
			ad_personalization: 'granted',
		}
	} else if (value == 'rejected') {
		return {
			ad_storage: 'denied',
			analytics_storage: 'denied',
			ad_user_data: 'denied',
			ad_personalization: 'denied',
		}
	}
	try {
		return JSON.parse(value) as CookieConsent | {}
	} catch (e) {
		console.warn('Undefined consent')
		return null
	}
}

export const storeCookieSelection = writable(
	getLocalStorage('storeCookieSelection', '{}') ? loadOldConsent(getLocalStorage('storeCookieSelection', '{}')) : null,
)
storeCookieSelection.subscribe((cookie_selection) => {
	if (browser) {
		if (cookie_selection) {
			localStorage.storeCookieSelection = JSON.stringify(cookie_selection)
		}
	}
})

/* ================================ */
/*   CATEGORY PAGE FILTERS
/* ================================ */

/* Active search value */
export const storeSearchedText = writable('')

/* Category price filters (price) */
export const storeMaxPrice = writable(0)

/* Category current page */
export const storeCurrentPage: Writable<number> = writable()

/* Category next page URL */
export const storeNextPageURL = writable('')

/* Category next page URL */
export const storeActiveFilters: Writable<{
	min_price?: number
	max_price?: number
	subcats?: Category[]
	brands?: (BaseBrand & BrandLink)[]
}> = writable({})


/* ================================ */
/*   CHECKOUT
/* ================================ */

/* Do we have the iframe? */
export const storeCheckoutIframe: Writable<boolean> = writable(false as boolean)


/* ================================ */
/*   SEARCH MODAL
/* ================================ */

// storeCategorySearchFilter
export const storeCategorySearchFilter: Writable<null | { id: any } | any> = writable(null)
export const storeBrandSearchFilter: Writable<null | FullBrand> = writable(null)

/* Search modal */
export const storeSearchModal: Writable<boolean> = writable(false)

/* Store brands and subcats for modal search */
export const storeSearchFilters: Writable<{
	max_price?: number
	subcats?: Category[]
	brand_aggs?: Array<number>
	brands?: (BaseBrand & BrandLink)[]
	prev_filters?: Object
	suggestions?: Suggestion[]
}> = writable({})

/* Store recent searches */
export const storeRecentSearches: Writable<Array<string>> = writable(
	getLocalStorageJSON('storeRecentSearches', []),
)
storeRecentSearches.subscribe(function(searches) {
	if (browser) {
		return (localStorage.storeRecentSearches = JSON.stringify(
			searches.slice(0, 9),
		))
	}
})

/* Store current search values */
export const storeCurrentSearch: Writable<{
	min_price?: number
	max_price?: number
	subcats?: Category[]
	brands?: (BaseBrand & BrandLink)[]
}> = writable(
	getLocalStorageJSON('storeCurrentSearch', {}),
)
storeCurrentSearch.subscribe(function(filters) {
	if (browser) {
		return (localStorage.storeCurrentSearch = JSON.stringify(filters))
	}
})

/* Store product to add to wishlist after login */
export const storeProductToAdd: Writable<BaseProduct | null> = writable(null)

/* Store user wishlists if logged in */
export const storeWishlists: Writable<Array<Wishlist>> = writable([] as Wishlist[])

/* Store user active wishlist if logged in */
export const storeActiveWishlist: Writable<Wishlist> = writable({} as Wishlist)
